import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
} from '@tanstack/react-query';
import { removeRowsFromCache } from '../lib/list-cache-rows';

type Snapshot = [QueryKey, unknown];

export interface OptimisticContext {
  snapshots: Snapshot[];
}

interface UseOptimisticListMutationOptions<TData, TError, TVariables, TItem> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /**
   * Prefix key matched against every cached list query. All variants that
   * start with this key (including filter suffixes) are optimistically
   * updated, rolled back on error, and invalidated on settle.
   */
  listQueryKey: QueryKey;
  /**
   * Returns true for items that should be removed from the list for this
   * mutation. Called for every item across every matching cached query.
   */
  match: (item: TItem, variables: TVariables) => boolean;
  /** Extra query keys to invalidate after the mutation settles. */
  alsoInvalidate?: QueryKey[];
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
}

/**
 * useMutation wrapper for mutations that remove an item from one or more
 * cached lists (archive, reject, delete). The row disappears synchronously
 * on mutate so it cannot be clicked again during the refetch window.
 *
 * Supports three cache shapes, auto-detected per query:
 *   - flat array: TItem[]
 *   - infinite: { pages: PaginatedResponse<TItem>[]; pageParams: unknown[] }
 *   - single page: PaginatedResponse<TItem>
 *
 * Do not use for in-place status changes (e.g. mark invoice paid) - the
 * item should stay visible with its new state.
 */
export function useOptimisticListMutation<TData, TError, TVariables, TItem>(
  options: UseOptimisticListMutationOptions<TData, TError, TVariables, TItem>,
): UseMutationResult<TData, TError, TVariables, OptimisticContext> {
  const queryClient = useQueryClient();
  const { mutationFn, listQueryKey, match, alsoInvalidate, onSuccess, onError } = options;

  return useMutation<TData, TError, TVariables, OptimisticContext>({
    mutationFn,
    // Rollback runs in onSettled, not onError. Setting an internal onError
    // would suppress the global MutationCache error toast (see App.tsx),
    // because that gate keys on `mutation.options.onError` being defined.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });
      const snapshots: Snapshot[] = queryClient.getQueriesData({ queryKey: listQueryKey });
      queryClient.setQueriesData({ queryKey: listQueryKey }, (old: unknown) =>
        removeRowsFromCache<TItem>(old, (item) => match(item, variables)),
      );
      return { snapshots };
    },
    ...(onSuccess ? { onSuccess: (data, variables) => onSuccess(data, variables) } : {}),
    ...(onError ? { onError: (error, variables) => onError(error, variables) } : {}),
    onSettled: (_data, error, _variables, context) => {
      if (error && context) {
        for (const [key, data] of context.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      queryClient.invalidateQueries({ queryKey: listQueryKey });
      if (alsoInvalidate) {
        for (const key of alsoInvalidate) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });
}
